import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography
} from '@mui/material';
import {
  Add as AddIcon, UploadFile as UploadIcon, VisibilityOutlined as PreviewIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import BlockNoteContentEditor from 'components/shared/content/BlockNoteContentEditor';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import AssignDialog from 'components/nutrition/AssignDialog';
import AssignmentChip from 'components/nutrition/AssignmentChip';
import NutritionCardActions from 'components/nutrition/NutritionCardActions';
import ProgramDocument from 'components/nutrition/ProgramDocument';
import { loadDraft, saveDraft, clearDraft } from 'common/utils/editorDraft';
import {
  NUTRITION_PROGRAMS, CREATE_PROGRAM, UPDATE_PROGRAM, DELETE_PROGRAMS
} from 'api/graphql/nutrition';

const PER_PAGE = 15;
const shortName = (n) => (n && n.length > 22 ? `${n.slice(0, 12)}…${n.slice(-7)}` : n);

function ProgramEditor({ open, program = null, onClose }) {
  const { t } = useTranslation(['nutrition', 'buttons']);
  const isEdit = Boolean(program);
  const draftKey = `otx_nutri_program_${program?.id ?? 'new'}`;
  const [draft] = useState(() => loadDraft(draftKey));
  const [title, setTitle] = useState(draft?.title ?? program?.title ?? '');
  const [type, setType] = useState(draft?.type ?? program?.type ?? 'general');
  const [content, setContent] = useState(draft?.content ?? program?.content ?? '');
  const [pdf, setPdf] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const refetchQueries = [{ query: NUTRITION_PROGRAMS, variables: { first: PER_PAGE, page: 1, search: '' } }];
  const [createProgram, { loading: creating }] = useMutation(CREATE_PROGRAM, { refetchQueries });
  const [updateProgram, { loading: updating }] = useMutation(UPDATE_PROGRAM, { refetchQueries });

  // Borrador: persiste el texto mientras se edita; se limpia solo al guardar con éxito.
  useEffect(() => { saveDraft(draftKey, { title, type, content }); }, [draftKey, title, type, content]);

  const save = async () => {
    const input = { title: title.trim(), type, content };
    if (pdf) input.pdf_document = pdf;
    if (isEdit) await updateProgram({ variables: { id: program.id, input } });
    else await createProgram({ variables: { input } });
    clearDraft(draftKey);
    onClose(true);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{isEdit ? t('editProgram', { ns: 'nutrition', defaultValue: 'Editar programa' }) : t('newProgram', { ns: 'nutrition', defaultValue: 'Nuevo programa' })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('title', { ns: 'nutrition', defaultValue: 'Título' })} value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small" />
            <TextField select label={t('contentScope', { ns: 'nutrition', defaultValue: 'Alcance del contenido' })} value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 180 }} size="small">
              <MenuItem value="general">{t('typeGeneral', { ns: 'nutrition', defaultValue: 'General' })}</MenuItem>
              <MenuItem value="specific">{t('typeSpecific', { ns: 'nutrition', defaultValue: 'Específico' })}</MenuItem>
            </TextField>
          </Stack>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('recommendations', { ns: 'nutrition', defaultValue: 'Recomendaciones' })}</Typography>
            <BlockNoteContentEditor value={content} onChange={setContent} minHeight={300} lossless />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignSelf: 'flex-start' }}>
            <Button component="label" variant="outlined" color="inherit" startIcon={<UploadIcon />} sx={{ color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700, maxWidth: '100%', '& > span:last-of-type': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>
              {pdf ? shortName(pdf.name) : t('attachPdf', { ns: 'nutrition', defaultValue: 'Adjuntar PDF' })}
              <input hidden type="file" accept="application/pdf" onChange={(e) => setPdf(e.target.files?.[0] ?? null)} />
            </Button>
            <Button onClick={() => setShowPreview((v) => !v)} variant="text" color="inherit" startIcon={<PreviewIcon />} sx={{ color: 'secondary.main', textTransform: 'none', fontWeight: 700 }}>
              {showPreview ? t('hidePreview', { ns: 'nutrition', defaultValue: 'Ocultar vista previa' }) : t('preview', { ns: 'nutrition', defaultValue: 'Vista previa' })}
            </Button>
          </Stack>

          {showPreview && (
            <Box sx={{ mt: 1 }}>
              <ProgramDocument
                title={title || t('title', { ns: 'nutrition', defaultValue: 'Título' })}
                type={type}
                content={content}
                programId={program?.id ?? null}
                hasPdf={Boolean(program?.pdf_url)}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button color="inherit" onClick={() => onClose(false)} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>{t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}</Button>
        <Button variant="contained" color="primary" onClick={save} disabled={creating || updating || !title.trim()} sx={{ textTransform: 'none', fontWeight: 700 }} startIcon={(creating || updating) ? <CircularProgress size={14} color="inherit" /> : null}>{t('save', { ns: 'buttons', defaultValue: 'Guardar' })}</Button>
      </DialogActions>
    </Dialog>
  );
}
ProgramEditor.propTypes = { open: PropTypes.bool.isRequired, program: PropTypes.object, onClose: PropTypes.func.isRequired };

function NutritionPrograms() {
  const { t } = useTranslation(['nutrition']);
  const { data, loading, refetch } = useQuery(NUTRITION_PROGRAMS, { variables: { first: PER_PAGE, page: 1, search: '' }, fetchPolicy: 'cache-and-network' });
  const [deletePrograms] = useMutation(DELETE_PROGRAMS);
  const [editor, setEditor] = useState({ open: false, program: null });
  const [del, setDel] = useState({ open: false, id: null });
  const [assign, setAssign] = useState({ open: false, programId: null, assignments: [] });

  const rows = data?.nutritionPrograms?.data ?? [];

  const confirmDelete = async () => {
    if (del.id) await deletePrograms({ variables: { id: [del.id] } });
    setDel({ open: false, id: null });
    refetch();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setEditor({ open: true, program: null })} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {t('newProgram', { ns: 'nutrition', defaultValue: 'Nuevo programa' })}
        </Button>
      </Stack>

      {loading && rows.length === 0 && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>}
      {!loading && rows.length === 0 && <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{t('noProgramsAdmin', { ns: 'nutrition', defaultValue: 'No hay programas. Crea el primero.' })}</Box>}

      <Stack spacing={1.5}>
        {rows.map((p) => (
          <Accordion
            key={p.id}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main',
              borderLeft: '3px solid', borderLeftColor: 'gold.main', borderRadius: '14px !important',
              overflow: 'hidden', '&::before': { display: 'none' }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', my: 1, minWidth: 0 } }}>
              {/* El título manda y va SOLO en su línea: compartiendo fila con el chip y tres
                  botones quedaba en móvil reducido a una letra, que es justo lo que hace falta
                  para reconocer la card. Debajo, a quién le llega. */}
              <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{p.title}</Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <AssignmentChip assignments={p.assignments_info} />
                </Stack>
              </Box>
              <NutritionCardActions
                onAssign={() => setAssign({ open: true, programId: p.id, assignments: p.assignments_info ?? [] })}
                onEdit={() => setEditor({ open: true, program: p })}
                onDelete={() => setDel({ open: true, id: p.id })}
              />
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <ProgramDocument title={p.title} type={p.type} content={p.content} programId={p.id} hasPdf={Boolean(p.pdf_url)} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {editor.open && <ProgramEditor open={editor.open} program={editor.program} onClose={() => { setEditor({ open: false, program: null }); refetch(); }} />}
      {assign.open && <AssignDialog open={assign.open} programId={assign.programId} assignments={assign.assignments} onClose={(changed) => { setAssign({ open: false, programId: null, assignments: [] }); if (changed) refetch(); }} />}
      <ConfirmDeleteModal open={del.open} onClose={() => setDel({ open: false, id: null })} onConfirm={confirmDelete} count={1} />
    </Box>
  );
}

export default NutritionPrograms;
